import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { PricingRules } from "@/components/PricingRules";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import FullScreenLoader from "@/components/FullScreenLoader";
import { ArrowLeft, DollarSign, ShoppingBag, Tag, Plus, ListOrdered } from "lucide-react";

export default function CustomerDetail() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [products, setProducts] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [d, p, s] = await Promise.all([
      api.get(`/store/customers/${cid}`),
      api.get("/store/products"),
      api.get("/store/price-sheets"),
    ]);
    setC(d.data);
    setProducts(p.data);
    setSheets(s.data);
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  if (!c) return <FullScreenLoader />;

  const set = (k, v) => setC((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/store/customers/${cid}`, {
        name: c.name, email: c.email, phone: c.phone, address: c.address,
        pricing_group: c.pricing_group, price_sheet_id: c.price_sheet_id || null,
        notes: c.notes, tags: c.tags, status: c.status,
      });
      toast.success("Customer saved");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const priceFor = (pid) => c.custom_prices.find((x) => x.product_id === pid)?.price;

  const setPrice = async (pid, val) => {
    const price = val === "" ? null : parseFloat(val);
    await api.put(`/store/customers/${cid}/pricing`, { product_id: pid, price });
    toast.success("Price updated");
    load();
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <button onClick={() => navigate("/dashboard/customers")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-customers">
        <ArrowLeft className="h-4 w-4" /> Back to customers
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xl">{c.name?.[0]?.toUpperCase()}</div>
          <div><h1 className="text-2xl font-bold tracking-tight">{c.name}</h1><p className="text-muted-foreground">{c.email}</p></div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/dashboard/orders/new?customer=${cid}`}><Button className="rounded-lg" data-testid="quick-create-order"><Plus className="h-4 w-4 mr-1.5" /> Create order</Button></Link>
          <Link to={`/dashboard/orders?search=${encodeURIComponent(c.name)}`}><Button variant="outline" className="rounded-lg" data-testid="quick-view-orders"><ListOrdered className="h-4 w-4 mr-1.5" /> View orders</Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 rounded-xl border-border"><div className="flex items-center gap-2 text-muted-foreground text-sm"><DollarSign className="h-4 w-4" /> Lifetime</div><p className="text-xl font-bold mt-1">{money(c.lifetime_sales)}</p></Card>
        <Card className="p-4 rounded-xl border-border"><div className="flex items-center gap-2 text-muted-foreground text-sm"><ShoppingBag className="h-4 w-4" /> Orders</div><p className="text-xl font-bold mt-1">{c.total_orders}</p></Card>
        <Card className="p-4 rounded-xl border-border"><div className="flex items-center gap-2 text-muted-foreground text-sm"><Tag className="h-4 w-4" /> Price Sheet</div><p className="text-xl font-bold mt-1 truncate">{c.price_sheet_name || "Base price"}</p></Card>
      </div>

      <Card className="p-6 rounded-xl border-border space-y-4">
        <h3 className="font-semibold">Profile</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Name</Label><Input value={c.name} onChange={(e) => set("name", e.target.value)} className="h-11" data-testid="edit-customer-name" /></div>
          <div className="space-y-2"><Label>Email</Label><Input value={c.email} onChange={(e) => set("email", e.target.value)} className="h-11" /></div>
          <div className="space-y-2"><Label>Phone</Label><Input value={c.phone} onChange={(e) => set("phone", e.target.value)} className="h-11" /></div>
          <div className="space-y-2">
            <Label>Price Sheet</Label>
            <Select value={c.price_sheet_id || "none"} onValueChange={(v) => set("price_sheet_id", v === "none" ? null : v)}>
              <SelectTrigger className="h-11" data-testid="customer-sheet-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Base price (no sheet)</SelectItem>
                {sheets.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2"><Label>Address</Label><Input value={c.address} onChange={(e) => set("address", e.target.value)} className="h-11" /></div>
        <div className="space-y-2"><Label>Notes</Label><Textarea value={c.notes} onChange={(e) => set("notes", e.target.value)} rows={3} data-testid="edit-customer-notes" /></div>
        <div className="flex justify-end"><Button onClick={save} disabled={saving} data-testid="save-customer-btn">Save profile</Button></div>
      </Card>

      <Card className="p-6 rounded-xl border-border">
        <h3 className="font-semibold mb-1">Manual per-product prices</h3>
        <p className="text-sm text-muted-foreground mb-4">Set prices just for this customer. Leave blank for base price.</p>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {products.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 py-1.5">
              <span className="text-sm font-medium truncate">{p.name}</span>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-muted-foreground">{money(p.base_price)} →</span>
                <Input type="number" step="0.01" defaultValue={priceFor(p.id) ?? ""} placeholder="Base"
                  onBlur={(e) => setPrice(p.id, e.target.value)} className="h-9 w-28" data-testid={`cust-detail-price-${p.id}`} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <PricingRules scopeType="customer" scopeId={cid} />

      <Card className="rounded-xl border-border overflow-hidden">
        <div className="p-5 border-b border-border"><h3 className="font-semibold">Order history</h3></div>
        {c.orders.length === 0 ? <p className="p-6 text-sm text-muted-foreground text-center">No orders yet.</p> : (
          <div className="divide-y divide-border">
            {c.orders.map((o) => (
              <Link to={`/dashboard/orders/${o.id}`} key={o.id} className="flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors">
                <div><p className="font-medium text-sm">{o.order_number}</p><p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</p></div>
                <div className="flex items-center gap-3"><StatusBadge status={o.status} /><span className="font-semibold text-sm">{money(o.total)}</span></div>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
